import { enroll } from '@api/selfServiceServices';
import { AuthnMethod } from '@imprivata-cloud/authn';
import { InputBox } from '@imprivata-cloud/components';
import { jsonToBase64 } from '@imprivata-cloud/dataprivacy';
import { useQueryClient } from '@tanstack/react-query';
import { Form } from 'antd';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigateKeepSearch } from '@hooks/useNavigateKeepSearch';
import ContinueButton from '@/components/continueButton/ContinueButton';
import { useErrorContext } from '@/errorHandler/context/ErrorContext';
import { RootRoutes } from '@/index';
import { landingPageDataTanstackCacheKey } from '@/portal/containers/users/hooks/useAuthenticators';
import { prepareAuthnData } from '@/utils/utils';
import { type ApiError, AppError } from '.././../../../errorHandler/errors';

enum FieldNames {
  SERIAL_NUMBER = 'serialNumber',
  TOKEN_CODE = 'tokenCode',
}

interface FormValues {
  [FieldNames.SERIAL_NUMBER]: string;
  [FieldNames.TOKEN_CODE]: string;
}

const TRANSLATION_ROOT = 'self-service.auth-method.imprivata-id';
const SERIAL_NUMBER_DIGIT_LENGTH = 8;
const TOKEN_CODE_LENGTH = 6;

const EnrollImprivataId = () => {
  const { t } = useTranslation();
  const navigateKeepSearch = useNavigateKeepSearch();
  const [form] = Form.useForm<FormValues>();
  const [submitDisabled, setSubmitDisabled] = useState(true);
  const { emitError } = useErrorContext();
  const queryClient = useQueryClient();

  const navigateToLandingPage = useCallback(() => {
    navigateKeepSearch(RootRoutes.SELF_SERVICE);
  }, [navigateKeepSearch]);

  const handleSubmit = useCallback(
    async (values: FormValues) => {
      try {
        // Extract only digits from input
        const digits = values.serialNumber.replaceAll(/\D/g, '');

        // Add IMPR prefix before sending to backend
        const serialNumber = `IMPR${digits}`;

        await enroll(
          prepareAuthnData(
            AuthnMethod.IMPR_ID,
            jsonToBase64({ version: '1.0', serialNumber, totp: values[FieldNames.TOKEN_CODE] }),
          ),
        );

        queryClient.invalidateQueries({ queryKey: [landingPageDataTanstackCacheKey], exact: true });

        navigateToLandingPage();
      } catch (error) {
        emitError(new AppError(error as ApiError));
      }
    },
    [emitError, navigateToLandingPage, queryClient],
  );

  const handleValuesChange = useCallback((_: Partial<FormValues>, values: FormValues) => {
    const serialNumber = values[FieldNames.SERIAL_NUMBER] || '';
    const tokenCode = values[FieldNames.TOKEN_CODE] || '';

    // Extract only digits for validation
    const digits = serialNumber.replaceAll(/\D/g, '');

    const isValidSerialNumber = digits.length === SERIAL_NUMBER_DIGIT_LENGTH;
    const isValidTokenCode = tokenCode.length === TOKEN_CODE_LENGTH;

    setSubmitDisabled(!(isValidTokenCode && isValidSerialNumber));
  }, []);

  const normalizeTokenCode = useCallback((tokenCode: string) => {
    return tokenCode.replaceAll(/\D/g, '');
  }, []);

  const handleSerialNumberBlur = useCallback(() => {
    const currentValue = form.getFieldValue(FieldNames.SERIAL_NUMBER) || '';
    const trimmedValue = currentValue.trim();

    // Extract all digits from the input
    const digits = trimmedValue.replaceAll(/\D/g, '');

    // Clamp to max 8 digits to match validation
    const clampedDigits = digits.slice(0, SERIAL_NUMBER_DIGIT_LENGTH);

    if (clampedDigits) {
      // Add IMPR prefix and update the field
      form.setFieldValue(FieldNames.SERIAL_NUMBER, `IMPR${clampedDigits}`);
    }

    // Always run validation check, even if no digits (e.g., user typed "IMPR" only)
    const tokenCode = form.getFieldValue(FieldNames.TOKEN_CODE) || '';
    const isValidSerialNumber = clampedDigits.length === SERIAL_NUMBER_DIGIT_LENGTH;
    const isValidTokenCode = tokenCode.length === TOKEN_CODE_LENGTH;
    setSubmitDisabled(!(isValidTokenCode && isValidSerialNumber));
  }, [form]);

  return (
    <div>
      <h1 className="title">{t(`${TRANSLATION_ROOT}.title`)}</h1>
      <ol className="sub-title sub-title-list">
        <li>{t(`${TRANSLATION_ROOT}.field-1`)}</li>
        <li>{t(`${TRANSLATION_ROOT}.field-2`)}</li>
        <li>{t(`${TRANSLATION_ROOT}.field-3`)}</li>
      </ol>
      <Form<FormValues> form={form} onValuesChange={handleValuesChange} onFinish={handleSubmit}>
        <Form.Item name={FieldNames.SERIAL_NUMBER}>
          <InputBox
            label={t(`${TRANSLATION_ROOT}.serial-number-label`)}
            autoFocus
            required
            type="text"
            maxLength={14}
            aria-label="serial-number"
            onBlur={handleSerialNumberBlur}
          />
        </Form.Item>

        <Form.Item name={FieldNames.TOKEN_CODE} normalize={normalizeTokenCode}>
          <InputBox
            label={t(`${TRANSLATION_ROOT}.token-code-label`)}
            required
            type="text"
            aria-label="token-code"
            maxLength={TOKEN_CODE_LENGTH}
            inputMode="numeric"
            value={form.getFieldValue(FieldNames.TOKEN_CODE)}
          />
        </Form.Item>

        <ContinueButton
          aria-label="continue-button"
          htmlType="submit"
          disabled={submitDisabled}
          secondaryButton={{
            'aria-label': 'do-this-later-button',
            label: t('common.do-later'),
            onClick: () => navigateToLandingPage(),
          }}
        >
          {t('common.continue-button')}
        </ContinueButton>
      </Form>
    </div>
  );
};

export default EnrollImprivataId;
